import { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import OutlinedInput from '@mui/material/OutlinedInput'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { InstrumentStopReason } from '@brushfire/types'

import { ConfirmationModalIcon } from '@/shared/components/confirmation/ConfirmationModalIcon'
import {
  ConfirmationModalLayout,
  ConfirmationModalShell,
} from '@/shared/components/confirmation/ConfirmationModalShell'
import {
  confirmationBodySx,
  confirmationDangerButtonSx,
  confirmationHeadlineSx,
  confirmationSecondaryButtonSx,
} from '@/shared/components/confirmation/confirmationModalStyles'

const STOP_REASON_LABELS: Record<InstrumentStopReason, string> = {
  [InstrumentStopReason.SufficientResponsesCollected]: 'Sufficient responses collected',
  [InstrumentStopReason.ResearchObjectivesChanged]: 'Research objectives have changed',
  [InstrumentStopReason.BudgetConstraints]: 'Budget constraints',
  [InstrumentStopReason.InstrumentError]: 'Instrument contains an error or incorrect questions',
  [InstrumentStopReason.CampaignCancelled]: 'Campaign has been cancelled',
  [InstrumentStopReason.DuplicateInstrument]: 'Duplicate instrument — replaced by another',
  [InstrumentStopReason.LowResponseQuality]: 'Low response quality detected',
  [InstrumentStopReason.ComplianceLegalHold]: 'Compliance or legal hold',
  [InstrumentStopReason.ClientStakeholderRequest]: 'Client or stakeholder request',
  [InstrumentStopReason.Other]: 'Other',
}

function SelectDropdownIcon(props: object) {
  return <KeyboardArrowDownIcon {...props} sx={{ fontSize: 20, color: '#4a5565', mr: '12px' }} />
}

const labelSx = {
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#23262f',
  mb: '8px',
  display: 'block',
  textAlign: 'left',
} as const

const selectSx = {
  height: '46px',
  borderRadius: '10px',
  fontSize: '14px',
  fontWeight: 400,
  color: '#23262f',
  bgcolor: '#ffffff',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e5e7eb',
    borderWidth: '1px',
  },
  '& .MuiSelect-select': {
    py: '12px',
    px: '16px',
  },
} as const

const inputSx = {
  height: '46px',
  width: '100%',
  bgcolor: '#ffffff',
  borderRadius: '10px',
  '& .MuiOutlinedInput-input': {
    px: '16px',
    py: '12px',
    fontSize: '14px',
    fontWeight: 400,
    color: '#23262f',
    letterSpacing: '-0.1504px',
    lineHeight: 'normal',
    '&::placeholder': { color: '#9ca3af', opacity: 1 },
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e5e7eb',
    borderWidth: '1px',
  },
} as const

const selectMenuProps = {
  PaperProps: {
    sx: {
      borderRadius: '10px',
      mt: '4px',
      boxShadow: '0px 4px 12px rgba(13, 27, 42, 0.12)',
      '& .MuiMenuItem-root': {
        fontFamily: 'Inter, sans-serif',
        fontSize: '14px',
        lineHeight: '21px',
        color: '#0d1b2a',
      },
    },
  },
} as const

interface Props {
  open: boolean
  instrumentName: string
  onClose: () => void
  onConfirm: (body: {
    stopReason: InstrumentStopReason
    stopReasonOther?: string | null
  }) => void | Promise<void>
  confirming: boolean
}

export function StopInstrumentDialog({
  open,
  instrumentName: _instrumentName,
  onClose,
  onConfirm,
  confirming,
}: Readonly<Props>) {
  const [stopReason, setStopReason] = useState<InstrumentStopReason | ''>('')
  const [stopReasonOther, setStopReasonOther] = useState('')

  const reasonOptions = useMemo(
    () =>
      Object.values(InstrumentStopReason).map((value) => ({
        value,
        label: STOP_REASON_LABELS[value],
      })),
    [],
  )

  const otherRequired = stopReason === InstrumentStopReason.Other
  const otherValid = !otherRequired || stopReasonOther.trim().length > 0
  const canConfirm = stopReason !== '' && otherValid

  const reset = () => {
    setStopReason('')
    setStopReasonOther('')
  }

  const handleClose = () => {
    if (confirming) return
    reset()
    onClose()
  }

  const handleConfirm = () => {
    if (!canConfirm) return
    void Promise.resolve(
      onConfirm({
        stopReason,
        stopReasonOther: otherRequired ? stopReasonOther.trim() : null,
      }),
    ).finally(reset)
  }

  return (
    <ConfirmationModalShell
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={confirming}
      dialogTestId="stop-instrument-dialog"
      ariaLabelledBy="stop-instrument-headline"
      ariaDescribedBy="stop-instrument-body"
    >
      <ConfirmationModalLayout>
        <ConfirmationModalIcon variant="stop" />

        <Box
          id="stop-instrument-body"
          sx={{
            width: '100%',
            px: '24px',
            pt: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Typography id="stop-instrument-headline" component="h2" sx={confirmationHeadlineSx}>
            Stop Instrument
          </Typography>
          <Typography sx={{ ...confirmationBodySx, maxWidth: 388 }}>
            Stopping this instrument is permanent and cannot be undone. The instrument link will be
            invalidated immediately and the reserved budget will be returned to your account.
          </Typography>

          <Box sx={{ width: '100%', maxWidth: 388, mt: '12px' }}>
            <Typography
              component="label"
              htmlFor="stop-instrument-reason-select"
              sx={labelSx}
              id="stop-reason-label"
            >
              Reason for stopping{' '}
              <Box component="span" sx={{ color: '#ef4444' }}>
                *
              </Box>
            </Typography>
            <FormControl fullWidth>
              <Select
                id="stop-instrument-reason-select"
                labelId="stop-reason-label"
                displayEmpty
                value={stopReason}
                onChange={(e) => setStopReason(e.target.value as InstrumentStopReason)}
                disabled={confirming}
                IconComponent={SelectDropdownIcon}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography sx={{ fontSize: '14px', color: '#9ca3af', fontWeight: 400 }}>
                        Select a reason for stopping
                      </Typography>
                    )
                  }
                  return STOP_REASON_LABELS[selected as InstrumentStopReason]
                }}
                MenuProps={selectMenuProps}
                data-testid="stop-instrument-reason-select"
                sx={selectSx}
              >
                {reasonOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {otherRequired ? (
            <Box sx={{ width: '100%', maxWidth: 388 }}>
              <Typography
                component="label"
                htmlFor="stop-instrument-reason-other"
                sx={labelSx}
              >
                Please specify{' '}
                <Box component="span" sx={{ color: '#ef4444' }}>
                  *
                </Box>
              </Typography>
              <OutlinedInput
                id="stop-instrument-reason-other"
                fullWidth
                placeholder="Enter reason"
                value={stopReasonOther}
                onChange={(e) => setStopReasonOther(e.target.value)}
                disabled={confirming}
                inputProps={{ maxLength: 500 }}
                data-testid="stop-instrument-reason-other"
                sx={inputSx}
              />
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            width: '100%',
            px: 3,
            py: 3,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={confirming}
            data-testid="stop-instrument-cancel"
            sx={confirmationSecondaryButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!canConfirm || confirming}
            data-testid="stop-instrument-confirm"
            sx={confirmationDangerButtonSx}
          >
            Confirm Stop
          </Button>
        </Box>
      </ConfirmationModalLayout>
    </ConfirmationModalShell>
  )
}
